import { TILE } from '../config/tuning'
import type { World } from '../ecs/world'
import { PALETTE } from '../render/palette'
import { Tile } from '../world/tiles'
import { damageEntity } from './damage'
import { hurtPlayer } from './damage'

export function projectileSystem(world: World, dt: number): void {
  for (const pr of world.entities) {
    if (pr.kind !== 'projectile' || pr.dead || !pr.proj) continue
    pr.x += pr.vx * dt
    pr.y += pr.vy * dt
    pr.lifetime = (pr.lifetime ?? 0) - dt
    if (pr.lifetime <= 0) {
      pr.dead = true
      continue
    }

    // wall hit (participant shots chip destructible walls)
    const tx = Math.floor(pr.x / TILE)
    const ty = Math.floor(pr.y / TILE)
    const tile = world.map.at(tx, ty)
    if (tile === Tile.Wall || tile === Tile.Void || tile === Tile.Door || tile === Tile.LockedDoor || tile === Tile.Destructible) {
      if (tile === Tile.Destructible && pr.proj.faction === 'participant') {
        if (world.map.damageWall(tx, ty)) {
          world.burst(pr.x, pr.y, PALETTE.rubble, 14, 120, 0.6)
          world.bus.emit('secret_found', {})
        } else {
          world.burst(pr.x, pr.y, PALETTE.wallTop, 4, 60, 0.25, 2)
        }
      } else if (pr.proj.faction === 'participant') {
        // SEAM bolts ricochet: reflect off the wall instead of dying
        if ((pr.proj.bounces ?? 0) > 0) {
          pr.proj.bounces!--
          // reflect along whichever axis re-clears the wall (corner: both)
          const prevX = pr.x - pr.vx * dt
          const prevY = pr.y - pr.vy * dt
          const clearX = !world.map.solidAt(Math.floor(prevX / TILE), Math.floor(pr.y / TILE))
          const clearY = !world.map.solidAt(Math.floor(pr.x / TILE), Math.floor(prevY / TILE))
          if (clearY || !clearX) pr.vy = -pr.vy
          if (clearX || !clearY) pr.vx = -pr.vx
          pr.x = prevX
          pr.y = prevY
          pr.facing = Math.atan2(pr.vy, pr.vx)
          world.burst(pr.x, pr.y, PALETTE.projPlayer, 3, 60, 0.2, 2)
          continue
        }
        world.bus.emit('player_hit_wall_shot', {})
        world.burst(pr.x, pr.y, PALETTE.wallTop, 3, 50, 0.2, 2)
      }
      pr.dead = true
      continue
    }

    // entity hits
    if (pr.proj.faction === 'participant') {
      for (const e of world.entities) {
        if (e.dead || (e.kind !== 'enemy' && e.kind !== 'generator' && e.kind !== 'boss')) continue
        const r = e.radius + pr.radius
        if ((e.x - pr.x) ** 2 + (e.y - pr.y) ** 2 >= r * r) continue
        if (e.etype === 'auditor') {
          // shots pass a faint ripple through it — unkillable, message lands visually
          e.flash = 0.04
          continue
        }
        // LANCE bolts pierce: hit each target once, keep flying
        if (pr.proj.pierce) {
          if (pr.proj.hitIds!.includes(e.id)) continue
          pr.proj.hitIds!.push(e.id)
        }
        damageEntity(world, e, pr.proj.damage, 'shot')
        // micro-knockback along the shot direction sells the impact (never bosses)
        if (e.kind === 'enemy' && !e.dead) {
          const len = Math.hypot(pr.vx, pr.vy) || 1
          const kb = world.tuning.feel.hitKnockback
          world.map.moveCircle(e, (pr.vx / len) * kb, (pr.vy / len) * kb, e.wallClipped)
        }
        world.burst(pr.x, pr.y, PALETTE.projPlayer, 4, 80, 0.2, 2)
        if (pr.proj.pierce) continue
        pr.dead = true
        break
      }
    } else {
      for (const p of world.players()) {
        const r = p.radius + pr.radius
        if ((p.x - pr.x) ** 2 + (p.y - pr.y) ** 2 >= r * r) continue
        hurtPlayer(world, p, pr.proj.damage, 'a sentry bolt')
        pr.dead = true
        break
      }
    }
  }
}
